import {
  stopPerformanceMeasure,
  trackError,
  trackUserEvent,
} from '@vend/utilities'
import { vn } from 'vend-number'
import { useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import {
  usePaymentManager,
  useSaleManager,
  useTrainingModeManager,
} from '../../../../ng-compat/hooks'
import { SalePayment } from '../PaymentModal/types'
import { getOutstandingBalance } from '../../../../functions/sale'
import { WEB_REGISTER } from '../../../web-register.constant'
import { Sale } from '../../../../models/sale'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectSaleLocalId,
  selectPayAccountBalance,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { removeActiveTransactionDetails } from '../../../../functions/payments'
import { SaleManager } from '../../../../managers/sale-manager'
import {
  finishedProcessingAddPayment,
  selectProcessingAddPayment,
  startProcessingAddPayment,
} from '../../../../state/slices/uiStateSlice'
import { useGoConfirmation } from './useGoConfirmation'

const trackOpenSaleConfirmation = (saleID: string) => {
  trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Open Sale Confirmation', {
    id: saleID,
  })
}

const savePaymentToSale = (payment: SalePayment, saleManager: SaleManager) => {
  const {
    paymentAmount,
    paymentType,
    transactionData,
    paymentId,
    tipAmount,
    surchargeData,
    changeAmount,
  } = payment

  return saleManager.pay(
    paymentAmount,
    paymentType,
    transactionData,
    paymentId,
    tipAmount,
    surchargeData,
    changeAmount
  )
}

export const useHandleCompletePayment = (
  allowPageReload: () => void,
  clearSelectedPaymentType: () => void
) => {
  const saleManager = useSaleManager()
  const paymentManager = usePaymentManager()
  const saleLocalId = useAppSelector(selectSaleLocalId)
  const payAccountBalance = useAppSelector(selectPayAccountBalance)
  const trainingModeManager = useTrainingModeManager()
  const goToConfirmation = useGoConfirmation()

  // This can be removed once paymentInProgressRef fix is confirmed to reduce the duplicate counts further
  // But maybe worth keeping it around until a permanent fix which would be to disable the button as the payment
  // Processing starts.
  const processingAddPayment = useAppSelector(selectProcessingAddPayment)
  const dispatch = useDispatch()

  // These will persist through different renders of the component
  const lastCompletePayTimeRef = useRef(0)
  const paymentInProgressRef = useRef(false)

  const recordPaymentMetric = useCallback(
    (updatedSale: Sale) => {
      if (trainingModeManager.trainingModeEnabled) {
        return // Skip metrics in training mode
      }

      const completedPayment =
        updatedSale.payments[updatedSale.payments.length - 1]
      if (!completedPayment) {
        return // Safety check - no payment to record
      }

      const { paymentType } = completedPayment
      stopPerformanceMeasure('payment-tender', {
        moduleName: 'payment',
        metadata: {
          paymentTypeName: paymentType.name,
          paymentTypeId: paymentType.type_id,
          paymentDate: completedPayment.paymentDate,
          retailerPaymentTypeId: paymentType.id,
          salePaymentId: completedPayment.localId,
        },
      })
    },
    [trainingModeManager.trainingModeEnabled]
  )

  const shouldCompleteSale = useCallback((updatedSale: Sale) => {
    const balance = getOutstandingBalance(updatedSale)
    const inclusivePrice = updatedSale.finals.inclusivePrice

    const priceIsGreaterThanZero = vn(inclusivePrice).gte(0)
    const priceIsLessThanZero = vn(inclusivePrice).lt(0)
    const balanceIsLessThanZero = vn(balance).lte(0)
    const balanceIsZero = vn(balance).eq(0)

    // Sale is complete if:
    // - For regular sales (positive price): balance is zero or negative (paid with change)
    return (
      (priceIsGreaterThanZero && balanceIsLessThanZero) ||
      (priceIsLessThanZero && balanceIsZero)
    )
  }, [])

  const finaliseSale = useCallback(
    async (updatedSale: Sale) => {
      try {
        paymentManager.updateReceiptMessageOnSaleComplete()
        await saleManager.closeSale()
        trackOpenSaleConfirmation(updatedSale.localId)
        removeActiveTransactionDetails()
      } catch (error: any) {
        trackError(error, {
          data: { page: 'payment view', saleID: saleLocalId },
        })
        throw error // Let calling function know finalization failed
      }
    },
    [paymentManager, saleLocalId, saleManager]
  )

  const handleCompletePayment = useCallback(
    async (payment: SalePayment, clearSelectedPayment = true) => {
      const now = Date.now()
      const timeSinceLastCall = now - lastCompletePayTimeRef.current
      const isRapidDuplicate = timeSinceLastCall < 1000 // within 1 second
      if (processingAddPayment) {
        return
      }

      // Check if we are potentially double-firing due to a race-condition
      if (isRapidDuplicate && paymentInProgressRef.current) {
        const groupName = `[CompletePaymentRaceConditionType-${payment.paymentType.type_id}]`
        trackError(
          `${groupName} Duplicate payment attempt detected`,
          {
            data: {
              processingAddPaymentState: processingAddPayment,
              timeSinceLastCallMs: timeSinceLastCall,
              paymentData: {
                paymentAmount: payment.paymentAmount,
                paymentType: payment.paymentType.type_id,
                paymentDescription:
                  payment.paymentType?.payment_type?.description,
              },
              saleID: saleLocalId,
              isRapidDuplicate,
              paymentInProgress: paymentInProgressRef.current,
            },
          },
          // Tags for Raygun filtering
          ['duplicate_payment_anomaly_payment_hook']
        )
      }

      if (paymentInProgressRef.current) {
        return
      }

      // Update last call time
      lastCompletePayTimeRef.current = now

      paymentInProgressRef.current = true
      dispatch(startProcessingAddPayment())
      let shouldNavigate = false
      try {
        const updatedSale = await savePaymentToSale(payment, saleManager)
        recordPaymentMetric(updatedSale)
        if (shouldCompleteSale(updatedSale)) {
          // Sale-less ABP: no carrier sale was created — skip closeSale() which
          // would post a $0 sale to the backend. We still navigate to the
          // confirmation panel, which handles submitMonoliphForm + redirect.
          if (!payAccountBalance?.abpTransactionId) {
            await finaliseSale(updatedSale)
          }
          shouldNavigate = true
        }
      } catch (error: any) {
        trackError(error, {
          data: {
            paymentType: payment.paymentType,
            saleID: saleLocalId,
          },
        })
      } finally {
        paymentInProgressRef.current = false
        allowPageReload()
        if (clearSelectedPayment) {
          clearSelectedPaymentType()
        }
        if (shouldNavigate) {
          goToConfirmation()
        }
        setTimeout(() => {
          dispatch(finishedProcessingAddPayment())
        })
      }
    },
    [
      processingAddPayment,
      dispatch,
      saleManager,
      recordPaymentMetric,
      shouldCompleteSale,
      finaliseSale,
      saleLocalId,
      allowPageReload,
      clearSelectedPaymentType,
      goToConfirmation,
      payAccountBalance,
    ]
  )

  return { handleCompletePayment }
}
